import { oc } from "@orpc/contract";
import { z } from "zod";
import { ArPaymentInvoiceSummarySchema } from "./ar-payments.contract";
import { InboundEmailSchema } from "../accounts-payable/ap-invoices.contract";

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

export const RemittanceChannelSchema = z.enum([
  "email",
  "edi",
  "portal",
  "manual",
]);

export const RemittanceMatchStatusSchema = z.enum([
  "unmatched",
  "auto_matched",
  "matched",
  "ambiguous",
  "rejected",
]);

export const RemittanceLineItemKindSchema = z.enum([
  "invoice_payment",
  "deduction",
]);

export const RemittanceReviewFilterSchema = z.enum([
  "needs_review",
  "complete",
  "all",
]);

// ---------------------------------------------------------------------------
// Line item summary (aggregate counts per advice, used in list view)
// ---------------------------------------------------------------------------

export const RemittanceLineItemSummarySchema = z.object({
  total: z.int().nonnegative(),
  matched: z.int().nonnegative(),
  autoMatched: z.int().nonnegative(),
  unmatched: z.int().nonnegative(),
  ambiguous: z.int().nonnegative(),
  rejected: z.int().nonnegative(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListRemittancesInputSchema = z.object({
  reviewFilter: RemittanceReviewFilterSchema.optional(),
  channel: RemittanceChannelSchema.optional(),
  receivedDateFrom: z.string().optional(),
  receivedDateTo: z.string().optional(),
  search: z.string().optional(),
  sortBy: z
    .enum(["receivedAt", "statedTotalAmount", "statedCustomerName"])
    .optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

// Forward-declared inline (instead of using RemittanceMatchedCustomerSchema
// below) because that constant is defined further down. Same shape, kept in
// sync with the detail-side schema.
const RemittanceListMatchedCustomerSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const RemittanceListItemSchema = z.object({
  id: UUIDSchema,
  channel: RemittanceChannelSchema,
  receivedAt: z.string(),
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: DecimalStringSchema.nullable(),
  statedReferenceNo: z.string().nullable(),
  senderEmail: z.string().nullable(),
  linkedPaymentId: UUIDSchema.nullable(),
  linkedPaymentRef: z.string().nullable(),
  matchedCustomer: RemittanceListMatchedCustomerSchema.nullable(),
  lineItemSummary: RemittanceLineItemSummarySchema,
});

export const PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

export const ListRemittancesResponseSchema = z.object({
  remittances: z.array(RemittanceListItemSchema),
  pagination: PaginationSchema,
});

// ---------------------------------------------------------------------------
// Get (detail)
// ---------------------------------------------------------------------------

export const GetRemittanceInputSchema = z.object({
  id: UUIDSchema,
});

export const RemittanceLineItemSchema = z.object({
  id: UUIDSchema,
  remittanceAdviceId: UUIDSchema,
  rawInvoiceRef: z.string(),
  invoiceId: UUIDSchema.nullable(),
  statedAmount: DecimalStringSchema.nullable(),
  matchStatus: RemittanceMatchStatusSchema,
  matchReason: z.string().nullable(),
  kind: RemittanceLineItemKindSchema,
  invoice: ArPaymentInvoiceSummarySchema.nullable(),
});

export const RemittanceMatchedCustomerSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const RemittanceDetailSchema = z.object({
  id: UUIDSchema,
  channel: RemittanceChannelSchema,
  receivedAt: z.string(),
  statedCustomerName: z.string().nullable(),
  statedTotalAmount: DecimalStringSchema.nullable(),
  statedPaymentDate: z.string().nullable(),
  statedPaymentMethod: z.string().nullable(),
  statedReferenceNo: z.string().nullable(),
  senderEmail: z.string().nullable(),
  linkedPaymentId: UUIDSchema.nullable(),
  linkedPaymentRef: z.string().nullable(),
  matchedCustomer: RemittanceMatchedCustomerSchema.nullable(),
  /**
   * True when the linked payment has been reconciled against a bank-side
   * cash event (ar_bank_transactions). Indicates the bank has confirmed the
   * funds matching what the customer's remittance advice claimed.
   */
  bankCleared: z.boolean(),
  lineItems: z.array(RemittanceLineItemSchema),
  sourceEmail: InboundEmailSchema.nullable(),
});

export const GetRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const ConfirmRemittanceMatchInputSchema = z.object({
  lineItemId: UUIDSchema,
  invoiceId: UUIDSchema,
});

export const ConfirmRemittanceMatchResponseSchema = z.object({
  lineItem: RemittanceLineItemSchema,
  paymentApplicationCreated: z.boolean(),
});

export const RejectLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
});

export const RejectLineItemResponseSchema = z.object({
  lineItem: RemittanceLineItemSchema,
});

export const UndoRejectLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
});

export const UndoRejectLineItemResponseSchema = z.object({
  lineItem: RemittanceLineItemSchema,
});

export const LinkEmailInputSchema = z.object({
  remittanceAdviceId: UUIDSchema,
  inboundEmailId: UUIDSchema,
});

export const LinkEmailResponseSchema = z.object({
  success: z.boolean(),
});

export const OverrideRemittanceCustomerInputSchema = z.object({
  remittanceAdviceId: UUIDSchema,
  customerId: UUIDSchema,
});

export const OverrideRemittanceCustomerResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

export const DeleteRemittanceInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteRemittanceResponseSchema = z.object({
  success: z.literal(true),
});

// --- Manual create / edit ---------------------------------------------------

export const ManualRemittanceLineItemInputSchema = z.object({
  rawInvoiceRef: z.string().trim().min(1),
  statedAmount: DecimalStringSchema.nullable().optional(),
  invoiceId: UUIDSchema.nullable().optional(),
  kind: RemittanceLineItemKindSchema.optional(),
  deductionReason: z.string().nullable().optional(),
});

export const CreateRemittanceInputSchema = z.object({
  customerId: UUIDSchema.nullable().optional(),
  statedCustomerName: z.string().nullable().optional(),
  statedTotalAmount: DecimalStringSchema.nullable().optional(),
  statedPaymentDate: z.string().nullable().optional(),
  statedPaymentMethod: z.string().nullable().optional(),
  statedReferenceNo: z.string().nullable().optional(),
  lineItems: z.array(ManualRemittanceLineItemInputSchema),
});

export const CreateRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

export const UpdateRemittanceInputSchema = z.object({
  id: UUIDSchema,
  statedCustomerName: z.string().nullable().optional(),
  statedTotalAmount: DecimalStringSchema.nullable().optional(),
  statedPaymentDate: z.string().nullable().optional(),
  statedPaymentMethod: z.string().nullable().optional(),
  statedReferenceNo: z.string().nullable().optional(),
});

export const UpdateRemittanceResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

/**
 * Bulk add: append one or more line items to an existing remittance. The
 * invoice is chosen via picker (`invoiceId`); `rawInvoiceRef` is derived from
 * the invoice number server-side. `kind` is inferred from the sign of
 * `statedAmount` — a negative amount is a deduction, a positive amount an
 * invoice payment — and the amount is stored as its absolute value, matching
 * the ingest convention (positive amount + explicit `kind`).
 */
export const BulkRemittanceLineItemInputSchema = z.object({
  invoiceId: UUIDSchema,
  statedAmount: DecimalStringSchema.refine((s) => Number(s) !== 0, {
    message: "Amount cannot be zero",
  }),
});

export const AddRemittanceLineItemsInputSchema = z.object({
  remittanceAdviceId: UUIDSchema,
  lineItems: z.array(BulkRemittanceLineItemInputSchema).min(1),
});

export const UpdateRemittanceLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
  rawInvoiceRef: z.string().trim().min(1).optional(),
  statedAmount: DecimalStringSchema.nullable().optional(),
  invoiceId: UUIDSchema.nullable().optional(),
  kind: RemittanceLineItemKindSchema.optional(),
  deductionReason: z.string().nullable().optional(),
});

export const DeleteRemittanceLineItemInputSchema = z.object({
  lineItemId: UUIDSchema,
});

/** Line-item mutations return the refreshed parent remittance detail. */
export const RemittanceLineItemMutationResponseSchema = z.object({
  remittance: RemittanceDetailSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const arRemittancesContracts = {
  list: oc
    .input(ListRemittancesInputSchema)
    .output(ListRemittancesResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetRemittanceInputSchema)
    .output(GetRemittanceResponseSchema)
    .route({ method: "GET" }),
  confirmMatch: oc
    .input(ConfirmRemittanceMatchInputSchema)
    .output(ConfirmRemittanceMatchResponseSchema)
    .route({ method: "POST" }),
  rejectLineItem: oc
    .input(RejectLineItemInputSchema)
    .output(RejectLineItemResponseSchema)
    .route({ method: "POST" }),
  undoRejectLineItem: oc
    .input(UndoRejectLineItemInputSchema)
    .output(UndoRejectLineItemResponseSchema)
    .route({ method: "POST" }),
  linkEmail: oc
    .input(LinkEmailInputSchema)
    .output(LinkEmailResponseSchema)
    .route({ method: "POST" }),
  overrideCustomer: oc
    .input(OverrideRemittanceCustomerInputSchema)
    .output(OverrideRemittanceCustomerResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteRemittanceInputSchema)
    .output(DeleteRemittanceResponseSchema)
    .route({ method: "POST" }),
  create: oc
    .input(CreateRemittanceInputSchema)
    .output(CreateRemittanceResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateRemittanceInputSchema)
    .output(UpdateRemittanceResponseSchema)
    .route({ method: "POST" }),
  addLineItems: oc
    .input(AddRemittanceLineItemsInputSchema)
    .output(RemittanceLineItemMutationResponseSchema)
    .route({ method: "POST" }),
  updateLineItem: oc
    .input(UpdateRemittanceLineItemInputSchema)
    .output(RemittanceLineItemMutationResponseSchema)
    .route({ method: "POST" }),
  deleteLineItem: oc
    .input(DeleteRemittanceLineItemInputSchema)
    .output(RemittanceLineItemMutationResponseSchema)
    .route({ method: "POST" }),
} as const;

// ---------------------------------------------------------------------------
// Type exports
// ---------------------------------------------------------------------------

export type RemittanceChannel = z.infer<typeof RemittanceChannelSchema>;
export type RemittanceMatchStatus = z.infer<typeof RemittanceMatchStatusSchema>;
export type RemittanceReviewFilter = z.infer<
  typeof RemittanceReviewFilterSchema
>;
export type RemittanceListItem = z.infer<typeof RemittanceListItemSchema>;
export type RemittanceLineItemSummary = z.infer<
  typeof RemittanceLineItemSummarySchema
>;
export type ListRemittancesInput = z.infer<typeof ListRemittancesInputSchema>;
export type ListRemittancesResponse = z.infer<
  typeof ListRemittancesResponseSchema
>;
export type RemittanceDetail = z.infer<typeof RemittanceDetailSchema>;
export type RemittanceLineItem = z.infer<typeof RemittanceLineItemSchema>;
export type ConfirmRemittanceMatchInput = z.infer<
  typeof ConfirmRemittanceMatchInputSchema
>;
export type ConfirmRemittanceMatchResponse = z.infer<
  typeof ConfirmRemittanceMatchResponseSchema
>;
export type OverrideRemittanceCustomerInput = z.infer<
  typeof OverrideRemittanceCustomerInputSchema
>;
export type OverrideRemittanceCustomerResponse = z.infer<
  typeof OverrideRemittanceCustomerResponseSchema
>;
export type RemittanceLineItemKind = z.infer<
  typeof RemittanceLineItemKindSchema
>;
export type ManualRemittanceLineItemInput = z.infer<
  typeof ManualRemittanceLineItemInputSchema
>;
export type CreateRemittanceInput = z.infer<typeof CreateRemittanceInputSchema>;
export type CreateRemittanceResponse = z.infer<
  typeof CreateRemittanceResponseSchema
>;
export type UpdateRemittanceInput = z.infer<typeof UpdateRemittanceInputSchema>;
export type AddRemittanceLineItemsInput = z.infer<
  typeof AddRemittanceLineItemsInputSchema
>;
export type UpdateRemittanceLineItemInput = z.infer<
  typeof UpdateRemittanceLineItemInputSchema
>;
export type DeleteRemittanceLineItemInput = z.infer<
  typeof DeleteRemittanceLineItemInputSchema
>;
